import { FloorPlanEngine } from "@archilogic/floor-plan-sdk";
import { Box, Chip, CircularProgress, Grid, Paper, Stack, Typography } from "@mui/material";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CustomCard, StyledButton } from "../../App.styled";
import { getColorForArea, getFilter } from "../../helpers/functions";
import { useGetMeasurementsByFloorQuery } from "../../redux/dataApi";

export default function DigitalFloorPlan() {
  const mapContainerRef = useRef(null);
  const floorPlanRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const { t } = useTranslation();
  const calendarSelection = useSelector((state) => state.calendar.calendarSelection);
  const publishableToken = process.env.REACT_APP_ARCHILOGIC;
  const { buildings, selectedBuilding } = useSelector((state) => state.buildings);
  const building = buildings.find((b) => b.id === selectedBuilding.id);
  const sortedFloors = [...building.floors]
    .filter((item) => item.description !== "Central Installations")
    .sort((a, b) => a.description.localeCompare(b.description));
  const { id: tenantId } = useSelector((state) => state.session.selectedTenant);
  const [selectedFloor, setSelectedFloor] = useState(sortedFloors[0]);

  const labels = [
    { text: "0%-19%", color: "#a9e48f" },
    { text: "20% - 39%", color: "#ffedac" },
    { text: "40% - 59%", color: "#ffd18f" },
    { text: "60% - 79%", color: "#ffb48d" },
    { text: "80% - 100%", color: "#ff9a8e" }
  ];

  const {
    data: utilization,
    isFetching,
    isError
  } = useGetMeasurementsByFloorQuery(
    {
      filter: getFilter("Occupancy", calendarSelection, selectedFloor),
      locationId: selectedFloor.id,
      tenantId
    },
    {
      skip: !selectedFloor
    }
  );

  const colorizeArea = useCallback((spaceId, color) => {
    if (!floorPlanRef.current) return;
    const colorMap = {
      green: [169, 228, 144],
      yellow: [255, 237, 174],
      orange: [255, 209, 143],
      orangered: [255, 180, 141],
      red: [255, 155, 142],
      default: [229, 231, 233]
    };
    floorPlanRef.current.drawNodeUpdate(spaceId, { fill: colorMap[color] ?? colorMap.default });
  }, []);

  const handleMouseHover = useCallback(
    debounce(({ position, sourceEvent }) => {
      const spaces = floorPlanRef.current.getSpaces({
        select: { id: true, name: true, subCategory: true },
        where: { at: position }
      });
      if (spaces.length > 0) {
        const space = spaces[0];
        const utilizationValue = utilization?.areas.find((item) => item.spaceId === space.id);
        const rect = mapContainerRef.current.getBoundingClientRect();
        setTooltip({
          x: sourceEvent.clientX - rect.left,
          y: sourceEvent.clientY - rect.top,
          name: space.name,
          subCategory: space.subCategory,
          avg: utilizationValue?.dataBucket.avg ?? null
        });
      } else {
        setTooltip(null);
      }
    }, 100),
    [utilization]
  );

  useEffect(() => {
    const canRender = publishableToken && mapContainerRef.current && selectedFloor && !isFetching;
    const hasData = !!utilization && !isError;

    if (canRender && (hasData || isError)) {
      const startupOptions = {
        theme: {
          background: {
            color: "#ffffff"
          },
          elements: {
            roomStamp: {
              roomStampDisplay: ["name", "area"]
            }
          }
        }
      };
      floorPlanRef.current = new FloorPlanEngine({
        container: mapContainerRef.current,
        options: startupOptions
      });
      floorPlanRef.current
        .loadFloorById(selectedFloor.floorPlanId, {
          publishableAccessToken: publishableToken
        })
        .then(() => {
          floorPlanRef.current.zoomExtents();
          if (hasData) {
            utilization.areas.forEach((area) => {
              colorizeArea(area.spaceId, getColorForArea(area.dataBucket.avg));
            });
          }
        });

      floorPlanRef.current.on("mousemove", handleMouseHover);
      return () => {
        if (floorPlanRef.current) {
          floorPlanRef.current.off("mousemove", handleMouseHover);
          floorPlanRef.current = null;
        }
        if (mapContainerRef.current) {
          mapContainerRef.current.innerHTML = "";
        }
        setTooltip(null);
      };
    }
  }, [
    publishableToken,
    selectedFloor,
    utilization,
    isError,
    handleMouseHover,
    isFetching,
    colorizeArea
  ]);

  return (
    <CustomCard
      variant="outlined"
      sx={{
        height: "calc(100vh - 200px) !important",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box
        style={{
          width: "100%",
          height: "100%",
          padding: "18px",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Grid container direction="column" sx={{ height: "100%", flexWrap: "nowrap" }}>
          <Grid item>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                padding: "18px"
              }}
            >
              <Stack useFlexGap spacing={1} direction="row">
                {labels?.map((label) => (
                  <Chip
                    variant="outlined"
                    label={label.text}
                    sx={{ backgroundColor: label.color }}
                    key={label.text}
                  />
                ))}
              </Stack>
              <Stack useFlexGap spacing={1} direction="row">
                {sortedFloors?.map((floor, index) => (
                  <StyledButton
                    key={index}
                    selected={selectedFloor.id === floor.id}
                    onClick={() => {
                      setSelectedFloor(floor);
                    }}
                  >
                    {floor.description}
                  </StyledButton>
                ))}
              </Stack>
            </div>
          </Grid>
          <Grid item sx={{ flex: 1, minHeight: 0, padding: "20px" }}>
            <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
              {isFetching && (
                <CircularProgress
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)"
                  }}
                />
              )}
              <div
                ref={mapContainerRef}
                style={{ position: "absolute", height: "100%", width: "100%" }}
              />
              {tooltip && (
                <Paper
                  elevation={3}
                  sx={{
                    position: "absolute",
                    left: tooltip.x,
                    top: tooltip.y,
                    transform: "translate(-50%, calc(-100% - 8px))",
                    padding: "8px 12px",
                    pointerEvents: "none",
                    zIndex: 10,
                    minWidth: 200
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    {tooltip.name}
                  </Typography>
                  <Typography variant="body2">
                    <b>{t("utilization.title")}:</b>{" "}
                    {tooltip.avg !== null ? tooltip.avg.toFixed(2).replace(".", ",") : 0}%
                  </Typography>
                  <Typography variant="body2">
                    <b>{t("utilization.roomtype")}:</b>{" "}
                    {t(`utilization.rooms.${tooltip.subCategory}`)}
                  </Typography>
                </Paper>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </CustomCard>
  );
}
